import React, { useState } from 'react';
import PageWrapper from '../components/PageWrapper';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

// Star data for the cards - Image URLs set to your specified filenames!
const starTypesData = [
  {
    id: 'main-sequence',
    name: 'Main Sequence Stars',
    shortDescription: 'Like our Sun, these stars fuse hydrogen into helium in their cores.',
    longDescription: 'Main sequence stars constitute the vast majority of stars in the universe. Their energy comes from the nuclear fusion of hydrogen into helium in their cores. The size and temperature of a main sequence star depend on its mass; more massive stars are hotter and brighter. Our Sun is a prime example of a main sequence star, currently in the middle of its approximately 10-billion-year lifespan in this phase. The stability of these stars is maintained by a balance between the outward pressure from nuclear fusion and the inward gravitational pull.',
    imageUrl: 'star.png', // Your specified image name
    color: 'bg-yellow-600',
    borderColor: 'border-yellow-300/50'
  },
  {
    id: 'red-giants',
    name: 'Red Giants & Supergiants',
    shortDescription: 'Evolved stars that have exhausted hydrogen in their cores, expanding greatly.',
    longDescription: 'Red Giants and Supergiants are stars in later stages of stellar evolution. After a main sequence star exhausts the hydrogen fuel in its core, it begins to fuse helium or heavier elements. This process causes the outer layers of the star to expand dramatically and cool, giving it a reddish hue. Red Supergiants are even more massive versions that expand to immense sizes, sometimes engulfing the orbits of inner planets. These stars are temporary but visually spectacular phases in the life cycle of many stars, leading to diverse ends like white dwarfs or supernovae.',
    imageUrl: '1.png', // Your specified image name
    color: 'bg-red-700',
    borderColor: 'border-red-400/50'
  },
  {
    id: 'white-dwarfs',
    name: 'White Dwarfs',
    shortDescription: 'Dense remnants of low to medium-mass stars, slowly cooling over billions of years.',
    longDescription: 'A white dwarf is what remains of a star like our Sun after it has exhausted its nuclear fuel and shed its outer layers, forming a planetary nebula. It is extremely dense, packing a mass comparable to the Sun into a volume no larger than Earth. White dwarfs no longer undergo fusion and slowly radiate away their residual heat, gradually cooling over billions of years until they become "black dwarfs" (a theoretical stage never observed due to the universe\'s age). They are primarily composed of carbon and oxygen, supported against gravitational collapse by electron degeneracy pressure.',
    imageUrl: '2.png', // Your specified image name
    color: 'bg-blue-600',
    borderColor: 'border-blue-300/50'
  },
  {
    id: 'neutron-stars',
    name: 'Neutron Stars',
    shortDescription: 'Extremely dense remnants of massive stars, composed almost entirely of neutrons.',
    longDescription: 'Neutron stars are born from the catastrophic collapse of the core of a massive star during a supernova explosion. They are incredibly dense, with a mass greater than our Sun packed into a sphere only about 20 kilometers (12 miles) in diameter. A teaspoon of neutron star material would weigh billions of tons. They are composed almost entirely of neutrons, hence their name, and possess incredibly strong magnetic fields and rotate extremely rapidly. Pulsars are a type of neutron star that emits beams of electromagnetic radiation from their magnetic poles, observed as regular pulses as the star rotates.',
    imageUrl: '3.png', // Your specified image name
    color: 'bg-purple-700',
    borderColor: 'border-purple-300/50'
  },
 
];

// Sub-component for a single star card
function StarCard({ star, isExpanded, onToggleExpand }) {
  return (
    <motion.div
      layout
      className={`relative p-6 border rounded-lg shadow-xl ${star.borderColor} ${isExpanded ? 'col-span-full' : ''}`}
      transition={{ layout: { duration: 0.5, type: "spring", damping: 20, stiffness: 100 } }}
      style={{ overflow: 'hidden' }}
    >
      {/* Background Image Container */}
      <div
        className="absolute inset-0 transition-opacity duration-300 bg-center bg-cover rounded-lg opacity-60 group-hover:opacity-50"
        style={{ backgroundImage: `url(${star.imageUrl})` }}
      ></div>

      {/* Content Overlay - ensures text is readable over image */}
      <div className="relative z-10 flex flex-col h-full">
        <motion.h3
          layout="position"
          className="mb-3 text-2xl font-bold text-white-300"
        >
          {star.name}
        </motion.h3>
        <AnimatePresence mode="wait">
          {!isExpanded && (
            <motion.p
              key="short-desc"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="flex-grow text-white-400"
            >
              {star.shortDescription}
            </motion.p>
          )}
          {isExpanded && (
            <motion.p
              key="long-desc"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex-grow mt-4 text-white-400"
            >
              {star.longDescription}
            </motion.p>
          )}
        </AnimatePresence>
        <motion.button
          className="self-start px-4 py-2 mt-6 font-semibold text-white transition-colors duration-300 bg-indigo-500 rounded-full hover:bg-indigo-600"
          onClick={() => onToggleExpand(star.id)}
          layout="position"
        >
          {isExpanded ? 'View Less' : 'View More'}
        </motion.button>
      </div>
    </motion.div>
  );
}

export default function StarPage() {
  const [expandedCardId, setExpandedCardId] = useState(null);

  const handleToggleExpand = (id) => {
    setExpandedCardId(expandedCardId === id ? null : id);
  };

  return (
    <PageWrapper>
      <motion.div
        className="flex flex-col items-center justify-center min-h-screen p-8 text-white bg-gradient-to-br from-indigo-900 to-black"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <motion.h1
          className="mb-6 text-6xl font-extrabold text-cyan-400 drop-shadow-lg"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 120, damping: 10 }}
        >
          Explore the Majestic Stars
        </motion.h1>

        <motion.p
          className="max-w-3xl mb-12 text-xl text-center text-gray-300"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, type: "spring", stiffness: 100, damping: 10 }}
        >
          Dive into the fascinating world of stellar objects. Click "View More" on each card to learn about different types of stars and their incredible journeys across the cosmos.
        </motion.p>

        <motion.div
          layout
          className="grid w-full max-w-6xl grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          {starTypesData.map((star) => (
            <StarCard
              key={star.id}
              star={star}
              isExpanded={expandedCardId === star.id}
              onToggleExpand={handleToggleExpand}
            />
          ))}
        </motion.div>

        <Link to="/explore" className="px-8 py-3 mt-16 text-xl font-semibold text-white no-underline transition-all duration-300 ease-in-out bg-blue-600 rounded-full shadow-lg hover:bg-blue-700 hover:shadow-blue-500/50">
          Back to Explore
        </Link>
      </motion.div>
    </PageWrapper>
  );
}